import React, { useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import {
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

import TableRowSkeleton from "../../components/TableRowSkeleton";
import { StatusBadge } from "../../components/ui";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";
import { getTokens } from "../../styles/tokens";
import { statusLabel } from "./helpers";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    drawerPaper: {
      width: 480,
      maxWidth: "100vw",
      backgroundColor: t.paper,
      // a gaveta encosta na borda da janela: só o filete da esquerda
      border: 0,
      borderLeft: `1px solid ${t.line}`,
      borderRadius: 0,
      boxShadow: t.shadow.lift,
      display: "flex",
      flexDirection: "column",
    },
    header: {
      flex: "none",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 16,
      padding: "18px 22px 14px",
      backgroundColor: t.muted,
      borderBottom: `1px solid ${t.line}`,
    },
    headerText: { minWidth: 0 },
    title: {
      margin: 0,
      fontSize: 18,
      fontWeight: 600,
      lineHeight: 1.35,
      letterSpacing: "-0.01em",
      color: t.text,
    },
    subtitle: {
      margin: "3px 0 0",
      fontSize: 13,
      lineHeight: 1.45,
      color: t.textSecondary,
      overflowWrap: "anywhere",
    },
    close: { flex: "none", margin: "-4px -8px -4px 0", padding: 7, color: t.textSecondary },
    body: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    code: {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      fontSize: 13,
      fontWeight: 600,
    },
    empty: {
      padding: "40px 20px",
      textAlign: "center",
      fontSize: 13.5,
      color: t.textSecondary,
    },
  };
});

const voucherTone = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "usado" || s === "used") return "brand";
  if (s === "cancelado" || s === "canceled" || s === "cancelled") return "error";
  if (s === "disponivel" || s === "disponível" || s === "available" || s === "livre") return "success";
  return "neutral";
};

// Drawer with the codes/status of every voucher in a batch (GET /hotspot/voucher-batches/:id)
const BatchDetailDrawer = ({ open, batchId, batchName, onClose }) => {
  const classes = useStyles();
  const { datetimeToClient } = useDate();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !batchId) {
      setVouchers([]);
      return undefined;
    }
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/hotspot/voucher-batches/${batchId}`);
        if (active) setVouchers(Array.isArray(data?.vouchers) ? data.vouchers : []);
      } catch (err) {
        toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, batchId]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.drawerPaper }}>
      <div className={classes.header}>
        <div className={classes.headerText}>
          <h2 className={classes.title}>{i18n.t("hotspot.detail.title")}</h2>
          {batchName && (
            <p className={classes.subtitle}>
              {batchName}
              {!loading && vouchers.length > 0 ? ` · ${vouchers.length}` : ""}
            </p>
          )}
        </div>
        <Tooltip title={i18n.t("hotspot.buttons.close")}>
          <IconButton className={classes.close} onClick={onClose} aria-label={i18n.t("hotspot.buttons.close")}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
      <div className={classes.body}>
        {!loading && vouchers.length === 0 ? (
          <div className={classes.empty}>{i18n.t("hotspot.detail.empty")}</div>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("hotspot.detail.code")}</TableCell>
                <TableCell>{i18n.t("hotspot.detail.status")}</TableCell>
                <TableCell>{i18n.t("hotspot.detail.usedAt")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableRowSkeleton columns={3} />}
              {!loading &&
                vouchers.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell className={classes.code}>{v.codigo}</TableCell>
                    <TableCell>
                      <StatusBadge color={voucherTone(v.status)} label={statusLabel(v.status)} />
                    </TableCell>
                    <TableCell>{v.usadoEm ? datetimeToClient(v.usadoEm) : "-"}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </div>
    </Drawer>
  );
};

export default BatchDetailDrawer;
